import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtUsd } from '../../lib/formatHelpers';

/**
 * V2 PaymentRowCard — Carte rangée pour une ligne de paiement (commission/payout/etc).
 *
 * Pattern mobile-first inspiré de Black OS V2 manager-payments cards :
 *  - Header : titre (période / type) + montant principal
 *  - Sub-row : metadata (manager, status badge, nb créateurs)
 *  - Clickable pour expand/drill-down (callback onExpand)
 *
 * NoIRock R2 : si `amount` est null (chiffré non décrypté), affiche "—"
 * avec un badge subtil "AES" pour indiquer la donnée chiffrée.
 *
 * Props :
 *  - title         : string (ex. "Mars 2026 · MENA")
 *  - subtitle      : string (ex. "Manager — Sarah, 12 créateurs")
 *  - amount        : number | null  (USD)
 *  - secondary     : string (optional small text under amount)
 *  - status        : 'draft' | 'validated' | 'paid' | 'pending' | 'approved' | 'rejected'
 *  - expanded      : bool
 *  - onClick       : () => void
 *  - children      : optionnel — rendu seulement quand `expanded`
 */
const STATUS_BADGE = {
  draft:     { cls: 'bg-muted text-muted-foreground',   label: 'Draft' },
  validated: { cls: 'bg-primary/10 text-primary',       label: 'Validated' },
  paid:      { cls: 'bg-success/10 text-success',       label: 'Paid' },
  pending:   { cls: 'bg-warning/10 text-warning',       label: 'Pending' },
  approved:  { cls: 'bg-primary/10 text-primary',       label: 'Approved' },
  rejected:  { cls: 'bg-error/10 text-error',           label: 'Rejected' },
};

const PaymentRowCard = ({
  title,
  subtitle,
  amount = null,
  secondary,
  status,
  expanded = false,
  onClick,
  children,
}) => {
  const { t } = useTranslation();
  const badge = STATUS_BADGE[status] || null;
  const interactive = typeof onClick === 'function';

  return (
    <div className={`rounded-2xl border transition-colors ${expanded ? 'bg-card border-primary/30' : 'bg-card border-border'}`}>
      <button
        type="button"
        onClick={onClick}
        disabled={!interactive}
        className={`w-full text-left px-4 md:px-5 py-3 md:py-4 grid gap-3 ${
          interactive ? 'cursor-pointer hover:bg-muted/30 focus:outline-none focus:ring-1 focus:ring-primary rounded-2xl' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-foreground truncate">{title}</div>
            {subtitle && <div className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="font-heading font-bold text-lg md:text-xl text-foreground tabular-nums">
              {amount != null ? fmtUsd(amount, { decimals: 2 }) : (
                <span className="text-muted-foreground/80">—</span>
              )}
            </div>
            {amount == null && (
              <div className="mt-0.5">
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-accent/10 text-accent">
                  {t('paymentsV2.aesPending', 'AES')}
                </span>
              </div>
            )}
            {secondary && <div className="text-[10px] text-muted-foreground mt-0.5">{secondary}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {badge && (
            <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.cls}`}>
              {t(`paymentsV2.status.${status}`, badge.label)}
            </span>
          )}
          {interactive && (
            <span className="ml-auto text-[10px] text-muted-foreground/70">
              {expanded
                ? t('paymentsV2.collapse', 'Collapse ↑')
                : t('paymentsV2.expand', 'Details ↓')}
            </span>
          )}
        </div>
      </button>
      {expanded && children && (
        <div className="border-t border-border px-4 md:px-5 py-3 md:py-4 bg-background/40">
          {children}
        </div>
      )}
    </div>
  );
};

export default PaymentRowCard;
